import React, { useState, useEffect } from 'react'
import { Building2, ChevronLeft, Settings, Package, Container, ArrowRight } from 'lucide-react'
import { Button, cn } from '@wove/web-ui'
import { useNavigate, useParams } from 'react-router-dom'
import { useIntegrations } from '@wove/api'

interface IntegrationSelectorProps {
  title: string
  description: string
  icon: React.ReactNode
  onIntegrationSelected: (integrationId: string) => void
}

export default function IntegrationSelector({ 
  title, 
  description, 
  icon, 
  onIntegrationSelected 
}: IntegrationSelectorProps) {
  const navigate = useNavigate()
  const { integrations, isLoading, error } = useIntegrations()
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('')

  // Auto-select if only one integration
  useEffect(() => {
    if (integrations.length === 1) {
      setSelectedIntegrationId(integrations[0].id)
    }
  }, [integrations])

  const handleContinue = () => {
    if (selectedIntegrationId) {
      onIntegrationSelected(selectedIntegrationId)
    }
  }

  if (error) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2"
            onClick={() => navigate('/organization-settings')}
            aria-label="Back to organization settings"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center">
            {icon}
            <h1 className="text-2xl font-bold tracking-tight ml-3">{title}</h1>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Integrations
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Unable to load your integrations. Please try refreshing the page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2"
            onClick={() => navigate('/organization-settings')}
            aria-label="Back to organization settings"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center">
            {icon}
            <h1 className="text-2xl font-bold tracking-tight ml-3">{title}</h1>
          </div>
        </div>

        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading integrations...</span>
        </div>
      </div>
    )
  }

  if (integrations.length === 0) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2"
            onClick={() => navigate('/organization-settings')}
            aria-label="Back to organization settings"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center">
            {icon}
            <h1 className="text-2xl font-bold tracking-tight ml-3">{title}</h1>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                No Integrations Found
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>You need to set up at least one integration before you can configure {title.toLowerCase()}.</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="icon"
          className="mr-2"
          onClick={() => navigate('/organization-settings')}
          aria-label="Back to organization settings"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center">
          {icon}
          <h1 className="text-2xl font-bold tracking-tight ml-3">{title}</h1>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Select Integration
          </h2>
          <p className="text-gray-600">
            {description} Choose which integration you'd like to configure.
          </p>
        </div>

        <div className="space-y-3">
          {integrations.map((integration) => (
            <label
              key={integration.id}
              className={cn(
                "flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors",
                selectedIntegrationId === integration.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              )}
            >
              <input
                type="radio"
                name="integration"
                value={integration.id}
                checked={selectedIntegrationId === integration.id}
                onChange={(e) => setSelectedIntegrationId(e.target.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-900">
                  {integration.name}
                </div>
                <div className="text-sm text-gray-500">
                  {integration.integrationType} • ID: {integration.id}
                </div>
              </div>
            </label>
          ))}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => navigate('/organization-settings')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!selectedIntegrationId}
            className="group"
          >
            Continue
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  )
}